import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubPartnerAccountStatus, withStyles } from 'vopay';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useFetchPartnerAccounts from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import ProgramTermsCard from '@/components/Entities/Accounts/AccountsOverview/ProgramTermsCard';
import getProgramTermsVariant from '@/components/Entities/Accounts/AccountsOverview/programTermsVariant';
import PartnerStatusSummaryCard from './PartnerStatusSummaryCard';
import styles from './PartnersOverview.module.scss';

const cn = withStyles(styles);

type PartnersOverviewProps = {
  /** True until the status summary has responded, however it responded. */
  fetchingStatusSummary: boolean;
  numTotalPartnerAccounts: number;
  statusCounts: { [status: string]: number | undefined };
  /** The status tiles, in order — the same list the Status filter offers. */
  stages: readonly SubPartnerAccountStatus[];
  /** A tile filters the partner list to its status, or clears the filter when it is the selected one. */
  onStatusClick: (status: SubPartnerAccountStatus) => void;
  /** The status the list is filtered to; its tile reads as selected. */
  selectedStatus: SubPartnerAccountStatus | '';
};

/**
 * The two cards above the partner list (spec 5.2): the partner's own rev-share or minimum program
 * fee — the Accounts page's card, not a copy — and the status summary of the partner accounts it
 * created. The summary is fetched by the page, which offers the card's tiles as its Status filter.
 */
export default function PartnersOverview({
  fetchingStatusSummary,
  numTotalPartnerAccounts,
  statusCounts,
  stages,
  onStatusClick,
  selectedStatus,
}: PartnersOverviewProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.Overview' });
  const toast = useToast();

  const {
    response: { partnerTypeLabel },
  } = usePartnerData();

  const programTermsVariant = getProgramTermsVariant(partnerTypeLabel);

  // The program terms ride along on every account listing, which is where the Accounts page reads
  // them. This page lists no accounts, so it asks the same hook for the smallest listing there is.
  const { response: programTerms, fetching: fetchingProgramTerms, fetchPartnerAccounts } = useFetchPartnerAccounts();

  // The hook's `fetching` is false until the mount effect below runs; without this the card would
  // decide it has nothing to show, and hide, for one frame first.
  const [hasLoadedProgramTerms, setHasLoadedProgramTerms] = useState(false);

  // Only a partner type that carries a term needs the request; the card is hidden for the rest.
  useEffect(() => {
    if (!programTermsVariant) return;

    fetchPartnerAccounts({ Limit: 1, IncludeBalances: false, IncludeExtendedInfo: false, IncludeChildAccounts: false })
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('programTermsError', { defaultValue: 'Could not load your program terms: {{errorMessage}}', errorMessage }),
        );
      })
      .finally(() => setHasLoadedProgramTerms(true));
    // fetchPartnerAccounts and toast are deliberately left out: the hook and the provider recreate
    // them every render, so depending on either would re-fire the fetch on every render.
  }, [programTermsVariant, t]);

  return (
    <div className={cn('overview')}>
      {programTermsVariant && (
        <ProgramTermsCard
          variant={programTermsVariant}
          fetching={fetchingProgramTerms || !hasLoadedProgramTerms}
          revShare={programTerms?.RevShare ?? null}
          minimumProgramFee={programTerms?.MinimumProgramFee ?? null}
        />
      )}

      <PartnerStatusSummaryCard
        fetching={fetchingStatusSummary}
        numTotalPartnerAccounts={numTotalPartnerAccounts}
        statusCounts={statusCounts}
        stages={stages}
        onStatusClick={onStatusClick}
        selectedStatus={selectedStatus}
      />
    </div>
  );
}
